/* Współdzielone drobiazgi UI: nagłówki sekcji, animacja wejścia, akordeon,
   okruszki, przewijanie do góry przy zmianie trasy i lekki toast. */

import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  createContext,
  useContext,
} from "react";
import { Link, useLocation } from "react-router-dom";
import { renderInline } from "../inline-format.js";

/* --------------------------------------------------- przewijanie do góry */

export function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        // bez `return` przed scrollIntoView: efekt może zwrócić wyłącznie funkcję sprzątającą albo nic — zwrócona
        // wartość (np. Promise w niektórych przeglądarkach) wywalała aplikację przy następnej zmianie strony
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return undefined;
      }
    }
    window.scrollTo({ top: 0, left: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [pathname, hash]);
  return null;
}

/* ----------------------------------------------- pojawianie się przy scrollu */

export function Reveal({ children, as: Tag = "div", delay = 0, className = "" }) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || seen) return;
    if (typeof IntersectionObserver === "undefined") return setSeen(true);
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setSeen(true);
          io.disconnect();
        }
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.05 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [seen]);

  return (
    <Tag
      ref={ref}
      className={`reveal ${seen ? "in" : ""} ${className}`.trim()}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </Tag>
  );
}

/* ------------------------------------------------------- nagłówek sekcji */

export function SectionHead({ eyebrow, title, lede, center, children }) {
  return (
    <Reveal className={`sec-head${center ? " center" : ""}`}>
      {eyebrow && <p className="eyebrow">{eyebrow}</p>}
      {title && <h2>{title}</h2>}
      {lede && <p className="lede">{renderInline(lede)}</p>}
      {children}
    </Reveal>
  );
}

/* -------------------------------------------------------------- okruszki */

export function Crumbs({ items = [] }) {
  return (
    <nav className="crumbs" aria-label="Ścieżka nawigacji">
      <Link to="/">Strona główna</Link>
      {items.map((it, i) => (
        <React.Fragment key={it.label}>
          <span aria-hidden="true">/</span>
          {it.to && i < items.length - 1 ? (
            <Link to={it.to}>{it.label}</Link>
          ) : (
            <span style={{ opacity: 1, margin: 0 }}>{it.label}</span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}

/* ------------------------------------------------- hero podstrony (krótki) */

export function PageHero({ eyebrow, title, lead, crumbs, children }) {
  return (
    <section className="phero">
      <div className="phero-inner">
        {crumbs && <Crumbs items={crumbs} />}
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h1>{title}</h1>
        {lead && <p className="lead">{renderInline(lead)}</p>}
        {children}
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- akordeon */

export function Accordion({ items, idPrefix = "acc" }) {
  const [open, setOpen] = useState(null);
  return (
    <div className="acc">
      {items.map((it, i) => {
        const isOpen = open === i;
        return (
          <div className={`acc-item${isOpen ? " open" : ""}`} key={it.id || i}>
            <h3 style={{ margin: 0 }}>
              <button
                type="button"
                className="acc-q"
                aria-expanded={isOpen}
                aria-controls={`${idPrefix}-${i}`}
                onClick={() => setOpen(isOpen ? null : i)}
              >
                {it.q}
              </button>
            </h3>
            {isOpen && (
              <div className="acc-a" id={`${idPrefix}-${i}`}>
                <p>{it.a}</p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ----------------------------------------------------------------- toast */

const ToastCtx = createContext(() => {});
export const useToast = () => useContext(ToastCtx);

export function ToastHost({ children }) {
  const [msg, setMsg] = useState(null);
  const timer = useRef(null);

  const push = useCallback((text) => {
    setMsg(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setMsg(null), 2600);
  }, []);

  useEffect(() => () => clearTimeout(timer.current), []);

  return (
    <ToastCtx.Provider value={push}>
      {children}
      {msg && (
        <div className="toast" role="status">
          {msg}
        </div>
      )}
    </ToastCtx.Provider>
  );
}

/* ---------------------------------------------- potwierdzenie usunięcia */

export function useConfirm() {
  return useCallback(
    (question) => window.confirm(question),
    []
  );
}

/* ------------------------------------------------------------------ modal */

/**
 * Okno modalne z zamykaniem przez tło, przycisk i Escape. Blokuje przewijanie
 * strony pod spodem, żeby podgląd nie gubił pozycji na stronie wywołującej.
 */
export function Modal({ title, onClose, children, wide }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div className="modal-veil" onClick={onClose}>
      <div
        className={`modal-panel${wide ? " wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <b>{title}</b>
          <button type="button" className="modal-x" aria-label="Zamknij" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}
